import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import AuthPage from './pages/DevAuthPage';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import AdminConsole from './pages/AdminConsole';
import Dashboard from './pages/Dashboard';
import SetupOrgPage from './pages/SetupOrgPage';
import InviteAccept from './pages/InviteAccept';
import ProgramScheduleEditPage from './pages/ProgramScheduleEditPage';
import RolePicker from './pages/RolePicker';
import PublicBookingPage from './pages/public/PublicBookingPage';
import PublicBookMePage from './pages/PublicBookMePage';
import PublicSurveyPage from './pages/PublicSurveyPage';
import PublicManageBookingPage from './pages/public/PublicManageBookingPage';
import PublicRegisterPage from './pages/public/PublicRegisterPage';
import RegPhotoPage from './pages/public/RegPhotoPage';
import RegPreviewPage from './pages/public/RegPreviewPage';
import PublicPayPage from './pages/public/PublicPayPage';
import PublicBudgetPage from './pages/public/PublicBudgetPage';
import AccessLinkPage from './pages/public/AccessLinkPage';
import PhoneSignInPage from './pages/public/PhoneSignInPage';
import WpSignInPage from './pages/public/WpSignInPage';
import ApproveComputerPage from './pages/public/ApproveComputerPage';
import PaymentResult from './pages/public/PaymentResult';
import YouTubeOAuthCallback from './pages/YouTubeOAuthCallback';
import GCalOAuthCallback from './pages/GCalOAuthCallback';
import FacebookOAuthCallback from './pages/FacebookOAuthCallback';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';
import './App.css';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="ol-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // AUTH_SPEC logout flow: developer sessions (OLDEV) leave the
    // `ol_dev_session` breadcrumb, so logout bounces back to /dev. Only
    // the "Full Sign Out" button on /dev clears it → primary /login.
    let target = '/login';
    try {
      if (localStorage.getItem('ol_dev_session') === 'true') target = '/dev';
    } catch {}
    return <Navigate to={target} replace />;
  }

  return children;
}

// Public Route Component (redirect to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="ol-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    // AUTH_SPEC: Semprion HQ (OLDEV) accounts always route THROUGH /dev
    // even when they've already signed in via the public /login. This
    // gives developers a single landing page with quick-access demo
    // buttons + a Super User button.
    const isDev = !!user?.roles?.some(r => r.role_type === 'developer');
    if (isDev) return <Navigate to="/dev" replace />;
    if ((user?.roles?.length || 0) > 1 && !user?.active_role) return <Navigate to="/select-role" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route
        path="/dev"
        element={<AuthPage />}
      />
      {/* The console's panes are real URLs, as BOP's are (`/admin/billing`,
          `/admin/customers`, ...). They were React state on a single `/admin`
          route, so a pane could not be linked to, bookmarked, or reached with
          the back button — and omnileagues.com/pricing had nothing to point at.
          `/admin` still works and shows Overview. */}
      <Route
        path="/admin/:tab?"
        element={
          <ProtectedRoute>
            <AdminConsole />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/" 
        element={<Navigate to="/dashboard" replace />} 
      />
      {/* OL BookMe: the page the OPPOSING school lands on from their emailed
          link, and the survey page a family lands on. Both are public and
          tokenized — no account. These links are sent in email, so the routes
          must exist or the whole peer-facing half is a dead end. */}
      <Route path="/bookme/:token" element={<PublicBookMePage />} />
      <Route path="/survey/:token" element={<PublicSurveyPage />} />
      <Route path="/book/:orgId/:eventSlug" element={<PublicBookingPage />} />
      <Route path="/register/photo/:token" element={<RegPhotoPage />} />
      <Route path="/register-preview" element={<RegPreviewPage />} />
      <Route path="/pay/:token" element={<PublicPayPage />} />
      <Route path="/budget/:token" element={<PublicBudgetPage />} />
      <Route path="/a/:token" element={<AccessLinkPage />} />
      <Route path="/signin-phone" element={<PhoneSignInPage />} />
      <Route path="/wp-signin" element={<WpSignInPage />} />
      <Route path="/approve" element={<ApproveComputerPage />} />
      <Route path="/register/:orgId/:publicId" element={<PublicRegisterPage />} />
      <Route path="/payment-success" element={<PaymentResult ok={true} />} />
      <Route path="/payment-cancel" element={<PaymentResult ok={false} />} />
      <Route path="/manage/:token" element={<PublicManageBookingPage />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/program-schedule/:token" element={<ProgramScheduleEditPage />} />
      <Route path="/select-role" element={<ProtectedRoute><RolePicker /></ProtectedRoute>} />
      <Route
        path="/setup-org"
        element={
          <ProtectedRoute>
            <SetupOrgPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/youtube-oauth-callback"
        element={
          <ProtectedRoute>
            <YouTubeOAuthCallback />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gcal-oauth-callback"
        element={
          <ProtectedRoute>
            <GCalOAuthCallback />
          </ProtectedRoute>
        }
      />
      <Route
        path="/facebook-oauth-callback"
        element={
          <ProtectedRoute>
            <FacebookOAuthCallback />
          </ProtectedRoute>
        }
      />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route 
        path="*" 
        element={<Navigate to="/dashboard" replace />} 
      />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
